import React from 'react';

/**
 * FreeThrowSpeedProfile — Inter-phase speed visualization
 *
 * Horizontal timeline showing timing between each biomechanical marker.
 * Color-coded by duration relative to shooter baseline.
 * Overlay comparison: made (green) vs miss types (red/orange).
 */

const PHASE_LABELS = [
  { key: 'stance_to_dip_ms', label: 'Stance→Dip', short: 'Init' },
  { key: 'dip_to_raise_ms', label: 'Dip→Raise', short: 'Trans' },
  { key: 'raise_to_set_point_ms', label: 'Raise→Set', short: 'Arm↑' },
  { key: 'set_point_to_release_ms', label: 'Set→Rel', short: 'Push' },
  { key: 'release_to_follow_through_ms', label: 'Rel→FT', short: 'F.T.' },
  { key: 'release_to_heel_land_ms', label: 'Rel→Heel', short: 'Toe' },
];

const OUTCOME_COLORS = {
  made: '#22c55e',
  missed_short: '#ef4444',
  missed_long: '#f97316',
  missed_left: '#8b5cf6',
  missed_right: '#06b6d4',
  missed_rim_in_out: '#eab308',
  missed_airball: '#ec4899',
  not_visible: '#6b7280',
};

const cardStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: 20,
};

export default function FreeThrowSpeedProfile({ attempt, comparison, baseline }) {
  const speed = attempt?.inter_phase_speed || {};
  const maxDuration = Math.max(
    ...PHASE_LABELS.map(p => speed[p.key] || 0),
    200, // minimum scale
  );

  const getBarColor = (duration, baselineDuration) => {
    if (!baselineDuration || baselineDuration === 0) return '#6366f1';
    const ratio = duration / baselineDuration;
    if (ratio < 0.85) return '#22c55e'; // faster than baseline
    if (ratio > 1.15) return '#ef4444'; // slower than baseline
    return '#eab308'; // normal
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#fff' }}>
          Inter-Phase Speed Profile
        </h3>
        <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
          <span style={{ color: '#22c55e' }}>● Fast</span>
          <span style={{ color: '#eab308' }}>● Normal</span>
          <span style={{ color: '#ef4444' }}>● Slow</span>
        </div>
      </div>

      {/* Speed bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {PHASE_LABELS.map(phase => {
          const duration = speed[phase.key] || 0;
          const baselineDuration = baseline?.[phase.key];
          const width = maxDuration > 0 ? (duration / maxDuration) * 100 : 0;
          const color = getBarColor(duration, baselineDuration);

          return (
            <div key={phase.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 70, fontSize: 11, color: '#888', textAlign: 'right', flexShrink: 0 }}>
                {phase.short}
              </div>
              <div style={{ flex: 1, height: 20, background: 'rgba(255,255,255,0.03)', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(width, 100)}%`,
                  height: '100%',
                  background: `${color}44`,
                  borderRight: `2px solid ${color}`,
                  borderRadius: 4,
                  transition: 'width 0.3s ease',
                }} />
                {/* Comparison overlay */}
                {comparison && comparison[phase.key] > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: `${Math.min((comparison[phase.key] / maxDuration) * 100, 100)}%`,
                    width: 2,
                    height: '100%',
                    background: '#fff',
                    opacity: 0.5,
                  }} />
                )}
              </div>
              <div style={{ width: 55, fontSize: 11, color: '#ccc', textAlign: 'right', flexShrink: 0 }}>
                {duration > 0 ? `${(duration / 100).toFixed(1)}t` : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total shot speed */}
      <div style={{ marginTop: 16, padding: '8px 12px', background: 'rgba(99,102,241,0.1)', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#a5b4fc' }}>Total Shot Speed (Raise→Release)</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
          {speed.total_shot_speed_tenths > 0 ? `${speed.total_shot_speed_tenths.toFixed(1)}t` : '—'}
        </span>
      </div>

      {/* Speed profile badge */}
      {speed.speed_profile && speed.speed_profile !== 'uniform' && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
          Profile: <span style={{ color: '#a5b4fc' }}>{speed.speed_profile.replace(/_/g, ' ')}</span>
        </div>
      )}
    </div>
  );
}
